import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';

const PathLogger = ({ title }) => {
  const params = useParams();
  const location = useLocation();
  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', border: '2px solid #007bff', borderRadius: '8px', margin: '20px' }}>
      <h2 style={{ color: '#007bff' }}>{title}</h2>
      <p><strong>Actual Path:</strong> <code>{location.pathname}</code></p>
      {Object.keys(params).length > 0 && (
        <p><strong>Params Detected:</strong> <code>{JSON.stringify(params)}</code></p>
      )}
      <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#666' }}>
        Verified on hub.deepak.sh
      </div>
    </div>
  );
};

// Preserves the wildcard (*) portion when redirecting between path prefixes.
// e.g. /event/checkout/my-event → /events/checkout/my-event
const SplatRedirect = ({ to }) => {
  const { '*': splat } = useParams();
  return <Navigate to={splat ? `${to}/${splat}` : to} replace />;
};

// Simulates V1's WithPermission guard + downgradeUserPath logic.
//
// Auth token is set via localStorage.setItem('auth_token', 'test') in Playwright
// tests that require an authenticated session.
//
// Special unauthenticated redirects (mirrors V1's downgradeUserPath):
//   /user/events/details/*  →  /events/details/*   (public event mirror, strips /user)
//   /user/event/:slug       →  /event/details/:slug  (live event public redirect)
//   everything else         →  /connect
const Protected = ({ children }) => {
  const location = useLocation();
  const isAuthed = !!localStorage.getItem('auth_token');

  if (isAuthed) return children;

  const path = location.pathname;

  // /user/events/details/* → /events/details/* (strip /user prefix)
  if (path.startsWith('/user/events/details')) {
    return <Navigate to={path.slice('/user'.length)} replace />;
  }

  // /user/event/:slug → /event/details/:slug
  // Note: /event/details/:slug will then be caught by the /event/* SplatRedirect
  // → final URL becomes /events/details/:slug (two-step chain, matches V1 behaviour)
  const liveMatch = path.match(/^\/user\/event\/([^/]+)/);
  if (liveMatch) {
    return <Navigate to={`/event/details/${liveMatch[1]}`} replace />;
  }

  return <Navigate to="/connect" replace />;
};

// Simulates V1's WithoutUser guard.
// If user is already authenticated and visits /connect, redirect them to /user.
// This mirrors V1's RegisterLoginView being wrapped with <WithoutUser>.
const ConnectGuard = ({ children }) => {
  const isAuthed = !!localStorage.getItem('auth_token');
  if (isAuthed) return <Navigate to="/user" replace />;
  return children;
};

// Redirects /user/event/:prettyURL → /user/event/:prettyURL/live
// Uses explicit absolute path to avoid relative path resolution ambiguity in flat routes.
const LiveEventIndexRedirect = () => {
  const { prettyURL } = useParams();
  return <Navigate to={`/user/event/${prettyURL}/live`} replace />;
};

// Redirects to a target path while preserving query string and hash.
// V1 emails use /login?clt=..., /login?dealroomInvitation=..., etc.
// React Router v6's plain <Navigate to="/connect" /> drops search/hash,
// so this component reconstructs the full URL.
const NavigatePreserveQuery = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>

        {/* ── 1. ROOT & AUTH (public) ──────────────────────────────────────────── */}
        <Route path="/" element={<Navigate to="/user" replace />} />
        <Route path="/login" element={<NavigatePreserveQuery to="/connect" />} />
        <Route path="/register" element={<NavigatePreserveQuery to="/connect" />} />
        <Route path="/restore" element={<Navigate to="/connect/reset-password" replace />} />
        <Route path="/reset-password" element={<Navigate to="/connect/reset-password" replace />} />
        <Route path="/connect/*" element={<ConnectGuard><PathLogger title="Registration / Login Flow" /></ConnectGuard>} />

        {/* ── 2. USER DASHBOARD REDIRECTS (protected) ─────────────────────────── */}
        <Route path="/user/setup-account"
          element={<Protected><Navigate to="/user/settings/investment-accounts" replace /></Protected>} />
        <Route path="/user/account-settings/login-methods"
          element={<Protected><Navigate to="/user/settings" replace /></Protected>} />
        <Route path="/user/account-settings/*"
          element={<Protected><Navigate to="/user/settings/company-details" replace /></Protected>} />
        <Route path="/user/account-settings"
          element={<Protected><Navigate to="/user/settings/company-details" replace /></Protected>} />

        {/* Investor alias redirects */}
        <Route path="/user/investor/preferences"
          element={<Protected><Navigate to="/user/settings/investor-details" replace /></Protected>} />
        <Route path="/user/investor/form-profiles"
          element={<Protected><Navigate to="/user/settings/investment-accounts" replace /></Protected>} />
        <Route path="/user/investor/my-info"
          element={<Protected><Navigate to="/user/settings/investor-details" replace /></Protected>} />
        <Route path="/user/investor/hub"
          element={<Protected><Navigate to="/user/investor/opportunities" replace /></Protected>} />
        <Route path="/user/investor/marketplace"
          element={<Protected><Navigate to="/user/investor/opportunities" replace /></Protected>} />
        <Route path="/user/investor"
          element={<Protected><Navigate to="/user/investor/opportunities" replace /></Protected>} />

        {/* Event alias redirects (inside /user tree) */}
        {/* /user/event/details/:slug → /events/details/:slug (strips /user prefix) */}
        <Route path="/user/event/details/*"
          element={<Protected><SplatRedirect to="/events/details" /></Protected>} />
        <Route path="/user/events"
          element={<Protected><Navigate to="/user/events/details" replace /></Protected>} />

        {/* ── 3. DEAL ROOMS (protected) ────────────────────────────────────────── */}
        <Route path="/user/deal-rooms/setup-room"
          element={<Protected><PathLogger title="Setup Room" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/recent-activity/*"
          element={<Protected><PathLogger title="Deal Room Recent Activity" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/activity/*"
          element={<Protected><PathLogger title="Deal Room Activity" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/chat/*"
          element={<Protected><PathLogger title="Deal Room Chat" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/details/*"
          element={<Protected><PathLogger title="Deal Room Overview" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/documents/*"
          element={<Protected><PathLogger title="Deal Room Documents" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/questions/*"
          element={<Protected><PathLogger title="Deal Room Q&A" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/investor-relations/*"
          element={<Protected><PathLogger title="Deal Room Investor Relations" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/feed/*"
          element={<Protected><PathLogger title="Deal Room Feed" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/transactions/*"
          element={<Protected><PathLogger title="Deal Room Transactions" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/transit/*"
          element={<Protected><PathLogger title="Deal Room Transit" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/invest/*"
          element={<Protected><PathLogger title="Deal Room Invest" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/settings/*"
          element={<Protected><PathLogger title="Deal Room Settings" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/features/*"
          element={<Protected><PathLogger title="Deal Room Features" /></Protected>} />
        <Route path="/user/deal-rooms/:loadDealroom/*"
          element={<Protected><PathLogger title="Deal Room" /></Protected>} />
        <Route path="/user/deal-rooms"
          element={<Protected><PathLogger title="Deal Rooms List" /></Protected>} />

        {/* ── 4. INVESTMENTS (protected) ───────────────────────────────────────── */}
        <Route path="/user/investments/application/:Key"
          element={<Protected><PathLogger title="Investment Application" /></Protected>} />
        <Route path="/user/investments/edit-application/:Key"
          element={<Protected><PathLogger title="Edit Application" /></Protected>} />
        <Route path="/user/investments/transaction/:Key"
          element={<Protected><PathLogger title="Transaction" /></Protected>} />
        <Route path="/user/investments/share-certificate/:Key"
          element={<Protected><PathLogger title="Share Certificate" /></Protected>} />
        <Route path="/user/investments"
          element={<Protected><PathLogger title="Investments List" /></Protected>} />

        {/* ── 5. INVITATIONS (protected) ───────────────────────────────────────── */}
        <Route path="/user/invitation/:Key"
          element={<Protected><PathLogger title="Deal Room Invitation" /></Protected>} />
        <Route path="/user/transaction-invitation/:Key"
          element={<Protected><PathLogger title="Transaction Invitation" /></Protected>} />

        {/* ── 6. INVESTOR SECTION (protected) ─────────────────────────────────── */}
        <Route path="/user/investor/opportunities"
          element={<Protected><PathLogger title="Opportunities Marketplace" /></Protected>} />
        <Route path="/user/investor/partners/:slug"
          element={<Protected><PathLogger title="Partner Details" /></Protected>} />
        <Route path="/user/investor/partners"
          element={<Protected><PathLogger title="Partners Listing" /></Protected>} />
        <Route path="/user/investor/feed"
          element={<Protected><PathLogger title="News Feed" /></Protected>} />
        <Route path="/user/investor/form-profiles/:Key"
          element={<Protected><PathLogger title="Edit Form Profile" /></Protected>} />
        <Route path="/user/investor/my-investments/application/:Key"
          element={<Protected><PathLogger title="My Investment Application" /></Protected>} />
        <Route path="/user/investor/my-investments/transaction/:Key"
          element={<Protected><PathLogger title="My Transaction" /></Protected>} />
        <Route path="/user/investor/my-investments/share-certificate/:Key"
          element={<Protected><PathLogger title="My Share Certificate" /></Protected>} />
        <Route path="/user/investor/my-investments"
          element={<Protected><PathLogger title="My Investments" /></Protected>} />

        {/* ── 7. IR MESSAGE (protected) ────────────────────────────────────────── */}
        <Route path="/user/investor-message/:DealroomKey/:MessageID"
          element={<Protected><PathLogger title="IR Message in Browser" /></Protected>} />

        {/* ── 8. LIVE EVENTS (protected) ───────────────────────────────────────── */}
        {/* /user/event (exact, no slug) → redirect to /user (mirrors V1 LiveEvent index) */}
        <Route path="/user/event"
          element={<Protected><Navigate to="/user" replace /></Protected>} />
        <Route path="/user/event/:prettyURL/live/:sceneSlug"
          element={<Protected><PathLogger title="Live Event Scene" /></Protected>} />
        <Route path="/user/event/:prettyURL/live"
          element={<Protected><PathLogger title="Live Event Main Stage" /></Protected>} />
        <Route path="/user/event/:prettyURL/sponsors"
          element={<Protected><PathLogger title="Live Event Sponsors" /></Protected>} />
        <Route path="/user/event/:prettyURL/exhibitor-hall/*"
          element={<Protected><PathLogger title="Live Event Exhibitor Hall" /></Protected>} />
        <Route path="/user/event/:prettyURL/networking/*"
          element={<Protected><PathLogger title="Live Event Networking" /></Protected>} />
        <Route path="/user/event/:prettyURL/agenda/*"
          element={<Protected><PathLogger title="Live Event Agenda" /></Protected>} />
        <Route path="/user/event/:prettyURL"
          element={<Protected><LiveEventIndexRedirect /></Protected>} />
        <Route path="/user/event/:prettyURL/*"
          element={<Protected><PathLogger title="Live Event" /></Protected>} />

        {/* ── 9. USER EVENTS (protected) ───────────────────────────────────────── */}
        <Route path="/user/events/details/*"
          element={<Protected><PathLogger title="User Event Details" /></Protected>} />        <Route path="/user/events"
          element={<Protected><Navigate to="/user/events/details" replace /></Protected>} />
        {/* /user/events/* unmatched paths → /user/events/details (mirrors V1 catch-all) */}
        <Route path="/user/events/*"
          element={<Protected><Navigate to="/user/events/details" replace /></Protected>} />

        {/* ── 10. SETTINGS (protected) ─────────────────────────────────────────── */}
        <Route path="/user/settings/investor-details"
          element={<Protected><PathLogger title="Settings - Investor Details" /></Protected>} />
        <Route path="/user/settings/investment-accounts"
          element={<Protected><PathLogger title="Settings - Investment Accounts" /></Protected>} />
        <Route path="/user/settings/notifications"
          element={<Protected><PathLogger title="Settings - Notifications" /></Protected>} />
        <Route path="/user/settings/company-details/*"
          element={<Protected><PathLogger title="Settings - Company Details" /></Protected>} />
        <Route path="/user/settings/*"
          element={<Protected><PathLogger title="Settings" /></Protected>} />

        {/* ── 11. VENTURE DATA (protected) ─────────────────────────────────────── */}
        <Route path="/user/venture-data/person/:permalink/*"
          element={<Protected><PathLogger title="Venture: Person" /></Protected>} />
        <Route path="/user/venture-data/organization/:permalink/*"
          element={<Protected><PathLogger title="Venture: Organisation" /></Protected>} />
        <Route path="/user/venture-data/user/:permalink/*"
          element={<Protected><PathLogger title="Venture: CRIISP User" /></Protected>} />
        <Route path="/user/venture-data/*"
          element={<Protected><PathLogger title="Venture Data" /></Protected>} />

        {/* ── 12. OTHER USER ROUTES (protected) ────────────────────────────────── */}
        <Route path="/user/tasks"
          element={<Protected><PathLogger title="Tasks" /></Protected>} />
        <Route path="/user/email-status"
          element={<Protected><PathLogger title="Email Status" /></Protected>} />
        <Route path="/user/leads"
          element={<Protected><PathLogger title="Investor Leads" /></Protected>} />
        <Route path="/user/investor-wizard"
          element={<Protected><PathLogger title="Investor Wizard" /></Protected>} />
        <Route path="/user/chat/*"
          element={<Protected><PathLogger title="Chat" /></Protected>} />
        <Route path="/user/support"
          element={<Protected><PathLogger title="User Support" /></Protected>} />
        <Route path="/user/not-found"
          element={<Protected><PathLogger title="User 404 Not Found" /></Protected>} />
        <Route path="/user"
          element={<Protected><PathLogger title="Main Dashboard" /></Protected>} />
        {/* User tree catch-all: unknown /user/* paths redirect to /user/not-found (mirrors V1) */}
        <Route path="/user/*"
          element={<Protected><Navigate to="/user/not-found" replace /></Protected>} />

        {/* ── 13. ADMIN (protected) ────────────────────────────────────────────── */}
        <Route path="/admin/users/inspect/:UserID"
          element={<Protected><PathLogger title="Admin: User Inspector" /></Protected>} />
        <Route path="/admin/rooms/inspect/:dealroomID"
          element={<Protected><PathLogger title="Admin: Room Inspector" /></Protected>} />
        <Route path="/admin/accounts/inspect/:accountID/profile/:profileKey"
          element={<Protected><PathLogger title="Admin: Profile Inspector" /></Protected>} />
        <Route path="/admin/accounts/inspect/:accountID"
          element={<Protected><PathLogger title="Admin: Account Inspector" /></Protected>} />
        <Route path="/admin/keywords/:keywordID"
          element={<Protected><PathLogger title="Admin: Keyword Deal Rooms" /></Protected>} />
        <Route path="/admin/partners/edit/:partnerID"
          element={<Protected><PathLogger title="Admin: Partner Editor" /></Protected>} />
        <Route path="/admin/partners/new"
          element={<Protected><PathLogger title="Admin: New Partner" /></Protected>} />
        <Route path="/admin/partners"
          element={<Protected><PathLogger title="Admin: Partners" /></Protected>} />
        <Route path="/admin/events-system/groups/:EventGroupID"
          element={<Protected><PathLogger title="Admin: Event Group Editor" /></Protected>} />
        <Route path="/admin/events-system/:EventID/tickets"
          element={<Protected><PathLogger title="Admin: Event Tickets" /></Protected>} />
        <Route path="/admin/events-system/:EventID/exhibitors"
          element={<Protected><PathLogger title="Admin: Event Exhibitors" /></Protected>} />
        <Route path="/admin/events-system/:EventID/presenters"
          element={<Protected><PathLogger title="Admin: Event Presenters" /></Protected>} />
        <Route path="/admin/events-system/:EventID/agenda/day/:DayID/scene/:SceneID/group/:GroupID/entry/:EntryID"
          element={<Protected><PathLogger title="Admin: Agenda Entry (in Group)" /></Protected>} />
        <Route path="/admin/events-system/:EventID/agenda/day/:DayID/scene/:SceneID/group/:GroupID"
          element={<Protected><PathLogger title="Admin: Agenda Group" /></Protected>} />
        <Route path="/admin/events-system/:EventID/agenda/day/:DayID/scene/:SceneID/entry/:EntryID"
          element={<Protected><PathLogger title="Admin: Agenda Entry (in Scene)" /></Protected>} />
        <Route path="/admin/events-system/:EventID/agenda/day/:DayID/scene/:SceneID"
          element={<Protected><PathLogger title="Admin: Agenda Scene" /></Protected>} />
        <Route path="/admin/events-system/:EventID/agenda/day/:DayID"
          element={<Protected><PathLogger title="Admin: Agenda Day" /></Protected>} />
        <Route path="/admin/events-system/:EventID/agenda/*"
          element={<Protected><PathLogger title="Admin: Event Agenda" /></Protected>} />
        <Route path="/admin/events-system/:EventID"
          element={<Protected><PathLogger title="Admin: Event Editor" /></Protected>} />
        <Route path="/admin/events-system/*"
          element={<Protected><PathLogger title="Admin: Events System" /></Protected>} />
        <Route path="/admin/wi-website/:ListID"
          element={<Protected><PathLogger title="Admin: WI Website Page Editor" /></Protected>} />
        <Route path="/admin/wi-website/*"
          element={<Protected><PathLogger title="Admin: WI Website" /></Protected>} />
        <Route path="/admin/admins"
          element={<Protected><PathLogger title="Admin: Administrators" /></Protected>} />
        <Route path="/admin/sso/users"
          element={<Protected><PathLogger title="Admin: SSO Users" /></Protected>} />
        <Route path="/admin/sso/merge-users"
          element={<Protected><PathLogger title="Admin: Merge Users" /></Protected>} />
        <Route path="/admin/users"
          element={<Protected><PathLogger title="Admin: Users List" /></Protected>} />
        <Route path="/admin/rooms"
          element={<Protected><PathLogger title="Admin: Deal Rooms" /></Protected>} />
        <Route path="/admin/accounts"
          element={<Protected><PathLogger title="Admin: Accounts" /></Protected>} />
        <Route path="/admin/keywords"
          element={<Protected><PathLogger title="Admin: Keywords" /></Protected>} />
        <Route path="/admin/subscriptions"
          element={<Protected><PathLogger title="Admin: Subscriptions" /></Protected>} />
        <Route path="/admin/room-stats"
          element={<Protected><PathLogger title="Admin: Room Stats" /></Protected>} />
        <Route path="/admin/data-exports"
          element={<Protected><PathLogger title="Admin: Data Exports" /></Protected>} />
        <Route path="/admin/premium"
          element={<Protected><PathLogger title="Admin: Premium" /></Protected>} />
        <Route path="/admin/legals"
          element={<Protected><PathLogger title="Admin: Legals" /></Protected>} />
        <Route path="/admin/events-tags"
          element={<Protected><PathLogger title="Admin: Events Tags" /></Protected>} />
        <Route path="/admin/api-data"
          element={<Protected><PathLogger title="Admin: API Data" /></Protected>} />
        <Route path="/admin/abuse/*"
          element={<Protected><PathLogger title="Admin: Abuse" /></Protected>} />
        {/* Admin sub-route catch-alls (mirrors V1: unknown sub-paths redirect to parent listing) */}
        <Route path="/admin/users/*"
          element={<Protected><Navigate to="/admin/users" replace /></Protected>} />
        <Route path="/admin/rooms/*"
          element={<Protected><Navigate to="/admin/rooms" replace /></Protected>} />
        <Route path="/admin/accounts/*"
          element={<Protected><Navigate to="/admin/accounts" replace /></Protected>} />
        <Route path="/admin"
          element={<Protected><PathLogger title="Admin Home" /></Protected>} />
        {/* Admin catch-all: unknown /admin/* paths redirect to /admin (mirrors V1) */}
        <Route path="/admin/*"
          element={<Protected><Navigate to="/admin" replace /></Protected>} />

        {/* ── 14. PUBLIC / ANONYMOUS ROUTES ────────────────────────────────────── */}
        {/* Checkout sub-steps (explicit routes matching V1 EventRegistration router) */}
        <Route path="/events/checkout/:prettyURL/connect/*"
          element={<PathLogger title="Event Checkout: Login/Register" />} />
        <Route path="/events/checkout/:prettyURL/eventbrite"
          element={<PathLogger title="Event Checkout: Eventbrite Tickets" />} />
        <Route path="/events/checkout/:prettyURL/about-you"
          element={<PathLogger title="Event Checkout: About You" />} />
        <Route path="/events/checkout/:prettyURL/success/:sessionID"
          element={<PathLogger title="Event Checkout: Confirmation" />} />
        <Route path="/events/checkout/:prettyURL/*"
          element={<PathLogger title="Event Registration Checkout" />} />
        <Route path="/events/details/*"
          element={<PathLogger title="Event Details" />} />
        <Route path="/events/details"
          element={<PathLogger title="Event Details" />} />
        <Route path="/events"
          element={<Navigate to="/events/details" replace />} />
        {/* /events/* (unrecognised sub-path) → /events/details, mirrors V1 anonymous events catch-all */}
        <Route path="/events/*"
          element={<Navigate to="/events/details" replace />} />
        {/* /event/* → /events/* (path-preserving splat redirect, mirrors V1 /event → ../events) */}
        <Route path="/event/*"
          element={<SplatRedirect to="/events" />} />
        <Route path="/event"
          element={<Navigate to="/events/details" replace />} />

        <Route path="/sign/:Key"
          element={<PathLogger title="External Signing" />} />
        <Route path="/opt-out/investor-relations/:token"
          element={<PathLogger title="Unsubscribe IR (legacy token)" />} />
        <Route path="/opt-out/investor-relations"
          element={<PathLogger title="Unsubscribe IR" />} />
        <Route path="/opt-out"
          element={<PathLogger title="Unsubscribe" />} />
        <Route path="/support"
          element={<PathLogger title="Public Support" />} />

        {/* Firebase Function endpoints — these are server-side in V1 but Cloudflare
            will redirect them to hub, so the SPA must accept the path gracefully. */}
        <Route path="/confirm-email/*"
          element={<PathLogger title="Confirm Email (server-side)" />} />
        <Route path="/s/:shortLinkID"
          element={<PathLogger title="Short Link Resolver (server-side)" />} />
        <Route path="/login-token"
          element={<PathLogger title="Login Token (server-side)" />} />
        <Route path="/login-redirect"
          element={<PathLogger title="Login Redirect / OAuth Initiator (server-side)" />} />
        <Route path="/external-login"
          element={<PathLogger title="External Login (server-side)" />} />
        <Route path="/redirect"
          element={<PathLogger title="Generic Redirect Page" />} />
        <Route path="/redirect/*"
          element={<PathLogger title="Generic Redirect Page" />} />

        {/* ── 15. NOT FOUND ────────────────────────────────────────────────────── */}
        <Route path="/not-found"
          element={<PathLogger title="404 Not Found" />} />
        {/* Global catch-all — any path not matched above → /not-found */}
        <Route path="*"
          element={<Navigate to="/not-found" replace />} />

      </Routes>
    </BrowserRouter>
  );
}
